import React, { useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import moment from "moment";
import constants from "../../../constants";
import FileUploadIcon from "../../../assets/images/file_upload.svg";
import "./uploadModal.scss";
import Text from "components/Text";
import { MoreTagsFunction } from "accountOwner/pages/AddEvent/addEvents.functions";
import RolesModal from "pages/AccountPreferences/components/ManageAiDocuments/components/RolesModal";
import RolePopup from "accountOwner/pages/AddEvent/components/RolePopup";
import OfficesModal from "pages/AccountPreferences/components/ManageAiDocuments/components/OfficesModal";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import CustomDropdown from "components/Dropdown";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";

function UploadModal({
  isOpen,
  setIsOpen,
  onClose,
  onSave,
  existingDocuments = [],
  staffRoleData,
  officeData,
  categoryList,
  isEditMode = false,
  fromSuperAdmin,
  t,
}) {
  const updatedCategoryList = categoryList?.map(({ category, ...rest }) => ({
    ...rest,
    name: category,
  }));
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedOfficeOption, setSelectedOfficeOption] = useState("all");
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
  const [officePopupOpen, setOfficePopupOpen] = useState(false);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState([]);
  const [selectedRoleOption, setSelectedRoleOption] = useState("all");
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [rolePopupOpen, setRolePopupOpen] = useState(false);
  const [designationIds, setDesignationIds] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [fileFieldErrors, setFileFieldErrors] = useState({});
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const fileInputRef = useRef(null);
  const MAX_FILE_SIZE = constants.MAX_FILE_SIZE_512_MB;
  const ALLOWED_TYPES = constants.ALLOWED_TYPESALLOWED_TYPES;
  const ALLOWED_MIME_TYPES = constants.ALLOWED_MIME_TYPES;

  const validateFile = (file) => {
    const errors = [];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    if (
      !ALLOWED_TYPES.includes(fileExtension) &&
      !ALLOWED_MIME_TYPES.includes(file.type)
    ) {
      errors.push(t("aiAssistant.fileNotSupported"));
    }

    if (file.size > MAX_FILE_SIZE) {
      errors.push(t("aiAssistant.fileSizeExceeded"));
    }

    return errors;
  };

  const processFiles = async (files) => {
    let processedFiles = [];

    for (let i = 0; i < files.length; i++) {
      let file = files[i];

      const documentName = file.name
        .split(".")
        .slice(0, -1)
        .join(".")
        .toLowerCase();

      const fileObj = {
        id: Date.now() + Math.random(),
        file: file,
        documentName: documentName,
        documentPath: null,
        description: "",
        version: "",
        category: "",
        expiryDate: "",
        size: file.size,
        type: file.type,
        extension: "." + file.name.split(".").pop().toLowerCase(),
        uploadDate: new Date().toLocaleDateString(),
        errors: [],
        isUploaded: false,
      };

      const validationErrors = validateFile(file);
      fileObj.errors = [...validationErrors];

      processedFiles.push(fileObj);
    }

    setUploadedFiles((prev) => [...prev, ...processedFiles]);
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;

    if (files.length > 0) {
      processFiles(files);
    }

    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragOver(false);
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  // Fixed: Now handles different field types properly
  const handleFieldChange = (fileId, fieldName, value) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === fileId ? { ...file, [fieldName]: value } : file
      )
    );

    // Clear field-specific error when user types
    setFileFieldErrors((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [fieldName]: "",
      },
    }));
  };

  const validateFileFields = () => {
    const errors = {};
    let hasErrors = false;

    uploadedFiles.forEach((fileObj) => {
      if (fileObj.errors.length === 0) {
        const fileErrors = {};

        if (!fileObj.category) {
          fileErrors.category = t("fieldNotEmpty");
          hasErrors = true;
        }

        if (!fileObj.version || fileObj.version.trim() === "") {
          fileErrors.version = t("fieldNotEmpty");
          hasErrors = true;
        }

        if (!fileObj.expiryDate) {
          fileErrors.expiryDate = t("fieldNotEmpty");
          hasErrors = true;
        }

        if (Object.keys(fileErrors).length > 0) {
          errors[fileObj.id] = fileErrors;
        }
      }
    });

    setFileFieldErrors(errors);
    return !hasErrors;
  };

  const getFileIcon = (extension) => {
    const ext = extension.toLowerCase().replace(".", "");
    return ext.toUpperCase();
  };

  const getFileIconClass = (extension, hasErrors) => {
    if (hasErrors) return "file-icon error";

    const ext = extension.toLowerCase().replace(".", "");
    switch (ext) {
      case "pdf":
        return "file-icon pdf";
      case "docx":
        return "file-icon docx";
      case "txt":
        return "file-icon txt";
      default:
        return "file-icon";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = constants.sizeUnits;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSave = () => {
    try {
      setValidationError("");
      setFileFieldErrors({});

      if (
        selectedOfficeOption === "specific" &&
        selectedOfficeIds.length === 0
      ) {
        setValidationError(t("selectOfficeIds"));
        return;
      }

      if (selectedRoleOption === "specific" && designationIds.length === 0) {
        setValidationError(t("selectRoleIds"));
        return;
      }

      const validFiles = uploadedFiles.filter(
        (file) => file.errors.length === 0
      );

      if (validFiles.length === 0) return;

      // Validate file fields
      if (!validateFileFields()) {
        return;
      }

      setIsUploading(true);

      if (onSave) {
        const filesWithData = validFiles.map((file) => {
          const fileData = {
            file: file.file,
            documentPath: file.documentPath,
            documentName: file.documentName,
            DocumentName: file.documentName,
            Description: file.description,
            Version: file.version,
            Category: file.category,
            ExpirationDate: moment(file.expiryDate).format("YYYY-MM-DD"),
            IsForAllOffice: selectedOfficeOption === "all",
            IsForAllDesignation: selectedRoleOption === "all",
            isUploaded: file.isUploaded || false,
          };

          if (selectedRoleOption === "specific" && designationIds?.length > 0) {
            fileData.DesignationIds = designationIds.map(
              (item) => item.designationsId
            );
          }

          if (
            selectedOfficeOption === "specific" &&
            selectedOfficeIds?.length > 0
          ) {
            fileData.OfficeIds = selectedOfficeIds.map((item) => item.OfficeId);
          }

          return fileData;
        });

        onSave(filesWithData);
      }

      setShowSuccess(true);
      setTimeout(() => {
        setUploadedFiles([]);
        setShowSuccess(false);
        setIsUploading(false);
        setValidationError("");
        if (onClose) {
          onClose();
        }
      }, 2000);
    } catch (error) {
      console.error(error?.message);
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setUploadedFiles([]);
    setShowSuccess(false);
    setIsUploading(false);
    setValidationError("");
    setFileFieldErrors({});
    setSelectedOfficeOption("all");
    setSelectedOfficeIds([]);
    setSelectedRoleOption("all");
    setDesignationIds([]);
    if (onClose) {
      onClose();
    }
  };

  const validFiles = uploadedFiles.filter((file) => file.errors.length === 0);

  const RadioBtn = ({ name, groupName, value, selectedValue, onChange }) => (
    <div className="d-flex mb-2">
      <div className="ch-radio">
        <label className="mr-5 pb-0 pt-1">
          <input
            type="radio"
            name={groupName}
            value={value}
            checked={selectedValue === value}
            onChange={() => onChange(value)}
          />
          <span>{name}</span>
        </label>
      </div>
    </div>
  );

  const handlePopupToggle = (event, setter) => {
    event.stopPropagation();
    setInvitePopupPosition({
      top: 40,
      left: 40,
    });
    setter((prev) => !prev);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        className="upload-modal modal-dialog-centered modal-lg"
        toggle={() => setIsOpen(!isOpen)}
      >
        <div className="modal-header">
          <h4 className="modal-title">{t("aiAssistant.modalHeading")}</h4>
          <button
            className="close-btn"
            onClick={() => setIsOpen(!isOpen)}
            type="button"
          >
            ×
          </button>
        </div>

        <ModalBody>
          {showSuccess && (
            <div className="upload-success-message">
              <span className="success-icon">✓</span>
              {validFiles.length} document{validFiles.length > 1 ? "s" : ""}{" "}
              {t("aiAssistant.uploaded")}
            </div>
          )}

          {/* Validation Error Message */}
          {validationError && (
            <div className="alert alert-danger" role="alert">
              {validationError}
            </div>
          )}

          {!fromSuperAdmin && (
            <div className="radio_btn_role_office_container">
              <div>
                <Text
                  size="13px"
                  weight="400"
                  color="#79869A"
                  marginBottom="7px"
                >
                  {t("aiAssistant.forOffices")}
                </Text>
                <div className="two_col_grid_radio">
                  <RadioBtn
                    name="All Offices"
                    groupName="office"
                    value="all"
                    selectedValue={selectedOfficeOption}
                    onChange={setSelectedOfficeOption}
                  />
                  <RadioBtn
                    name="Specific Office"
                    groupName="office"
                    value="specific"
                    selectedValue={selectedOfficeOption}
                    onChange={setSelectedOfficeOption}
                  />
                </div>

                {selectedOfficeOption === "specific" && (
                  <div
                    className={`c-form-control link_btn_style`}
                    style={{ position: "relative" }}
                  >
                    <div className="d-flex justify-content-between">
                      <Text size="13px" weight="400" color="#79869A">
                        {t("notes.selectOffice")}
                        <span className="text-danger">*</span>
                      </Text>
                      <button
                        className="link-btn border-0 bg-transparent"
                        onClick={() => setIsOfficeModalOpen(true)}
                      >
                        {t("staff.select")}
                      </button>
                    </div>

                    <div style={{ position: "relative" }}>
                      {selectedOfficeIds.length > 0 ? (
                        <MoreTagsFunction
                          value={selectedOfficeIds}
                          clickHandler={(e) =>
                            handlePopupToggle(e, setOfficePopupOpen)
                          }
                          mapFn={(item) => item?.name || ""}
                          lastIndex={3}
                        />
                      ) : (
                        <Text
                          size="12px"
                          weight="400"
                          color="#999"
                          marginTop="8px"
                        >
                          {t("accountOwner.noOfficeSelected")}
                        </Text>
                      )}
                      {officePopupOpen && (
                        <InviteesPopup
                          position={invitePopupPosition}
                          handleInvitePopupToggle={(e) =>
                            handlePopupToggle(e, setOfficePopupOpen)
                          }
                          list={selectedOfficeIds}
                          showRoleAndOffice={false}
                          isForEvent={true}
                          onlyOfffice
                          isfromBusySlot
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ---- Roles ---- */}
              <div>
                <Text
                  size="13px"
                  weight="400"
                  color="#79869A"
                  marginBottom="7px"
                >
                  {t("aiAssistant.manageDocuments.selectRoles")}
                </Text>
                <div className="two_col_grid_radio">
                  <RadioBtn
                    name="All Roles"
                    groupName="role"
                    value="all"
                    selectedValue={selectedRoleOption}
                    onChange={setSelectedRoleOption}
                  />
                  <RadioBtn
                    name="Specific Role"
                    groupName="role"
                    value="specific"
                    selectedValue={selectedRoleOption}
                    onChange={setSelectedRoleOption}
                  />
                </div>

                {selectedRoleOption === "specific" && (
                  <div className={`c-form-control link_btn_style`}>
                    <div className="d-flex justify-content-between">
                      <Text size="12px" weight="400" color="#6F7788">
                        {t("aiAssistant.manageDocuments.selectRoles")}
                        <span className="text-danger">*</span>
                      </Text>
                      <button
                        className="link-btn border-0 bg-transparent"
                        onClick={() => setIsRolesModalOpen(true)}
                      >
                        {t("staff.select")}
                      </button>
                    </div>

                    <div
                      className="c-form-control "
                      style={{ position: "relative" }}
                    >
                      {designationIds.length > 0 ? (
                        <MoreTagsFunction
                          value={designationIds}
                          clickHandler={(e) =>
                            handlePopupToggle(e, setRolePopupOpen)
                          }
                          mapFn={(item) => item?.name || ""}
                          lastIndex={3}
                        />
                      ) : (
                        <Text
                          size="12px"
                          weight="400"
                          color="#999"
                          marginTop="8px"
                        >
                          {t("accountOwner.noRolesSelected")}
                        </Text>
                      )}
                      {rolePopupOpen && (
                        <RolePopup
                          handlePopupToggle={(e) =>
                            handlePopupToggle(e, setRolePopupOpen)
                          }
                          position={invitePopupPosition}
                          list={designationIds}
                          isForEvent={true}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className={`upload-area ${dragOver ? "drag-over" : ""} `}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            <div className="upload-icon">
              <img src={FileUploadIcon} alt="message template icon" />
            </div>
            <div className="upload-title">{t("aiAssistant.modalTitle")}</div>
            <div className="upload-subtitle">
              {t("aiAssistant.ModalSubTitle")}
            </div>
            <button
              type="button"
              className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              {t("aiAssistant.browseFile")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="file-input-hidden"
              multiple
              onChange={handleFileSelect}
            />
          </div>
          {/* File List */}
          {uploadedFiles.length > 0 && (
            <div className="file-list">
              {uploadedFiles.map((fileObj) => {
                const hasErrors = fileObj.errors.length > 0;

                return (
                  <div
                    key={fileObj.id}
                    className={`file-item ${hasErrors ? "error" : ""}`}
                  >
                    <div className="file-header">
                      <div className="file-info">
                        <div
                          className={getFileIconClass(
                            fileObj.extension,
                            hasErrors
                          )}
                        >
                          {hasErrors ? "⚠" : getFileIcon(fileObj.extension)}
                        </div>
                        <div className="file-details">
                          <div className="file-name">{fileObj?.file?.name}</div>
                          <div className="file-meta">
                            {moment(fileObj?.uploadDate, "M/D/YYYY").format(
                              "DD MMM, YYYY"
                            )}{" "}
                            |{" "}
                            {moment(
                              new Date(fileObj?.file?.lastModifiedDate)
                            ).format("h:mma")}{" "}
                            • {formatFileSize(fileObj.size)}{" "}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(fileObj.id)}
                        className="remove-btn"
                        title="Remove file"
                        type="button"
                      >
                        ×
                      </button>
                    </div>

                    {/* Error Messages */}
                    {hasErrors && (
                      <div className="error-message">
                        {fileObj.errors.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </div>
                    )}

                    {/* File Fields - Individual per file */}
                    {!hasErrors && (
                      <>
                        <div className="c-field">
                          <label>
                            {t("superAdmin.category")}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="custom-dropdown-only">
                            <CustomDropdown
                              options={updatedCategoryList}
                              selectedOption={fileObj.category}
                              selectOption={(id) => {
                                handleFieldChange(fileObj.id, "category", id);
                              }}
                              placeholder="Select"
                            />
                          </div>
                          {fileFieldErrors[fileObj.id]?.category && (
                            <div className="field-error-message">
                              {fileFieldErrors[fileObj.id].category}
                            </div>
                          )}
                        </div>
                        <div className="c-field">
                          <label>
                            {t("aiAssistant.uploadModal.version")}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            className="c-form-control input-bg-light-yellow"
                            name="version"
                            placeholder="Enter"
                            value={fileObj.version}
                            maxLength="5"
                            onChange={(e) =>
                              handleFieldChange(
                                fileObj.id,
                                "version",
                                e.target.value
                              )
                            }
                          />
                          {fileFieldErrors[fileObj.id]?.version && (
                            <div className="field-error-message">
                              {fileFieldErrors[fileObj.id].version}
                            </div>
                          )}
                        </div>
                        <div className="c-field">
                          <label>
                            {t("superAdmin.expireDate")}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="d-flex inputdate">
                            <ReactDatePickerCommon
                              dateFormat="dd-MM-yyyy"
                              className="c-form-control"
                              onChange={(date) => {
                                handleFieldChange(
                                  fileObj.id,
                                  "expiryDate",
                                  date
                                );
                              }}
                              minDate={moment().add(1, "day").toDate()}
                              selected={fileObj.expiryDate}
                            />
                          </div>
                          {fileFieldErrors[fileObj.id]?.expiryDate && (
                            <div className="field-error-message">
                              {fileFieldErrors[fileObj.id].expiryDate}
                            </div>
                          )}
                        </div>
                        <div className="c-field">
                          <label>{t("aiAssistant.description")}</label>
                          <textarea
                            className="c-form-control input-bg-light-yellow"
                            name="description"
                            rows="3"
                            maxLength="1000"
                            placeholder="Enter"
                            value={fileObj.description}
                            onChange={(e) =>
                              handleFieldChange(
                                fileObj.id,
                                "description",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            onClick={handleSave}
            disabled={validFiles.length === 0 || isUploading}
            title="Save"
          >
            {isEditMode ? t("update") : t("save")}
          </button>
          <button
            className="button button-round button-border button-dark btn-mobile-link"
            onClick={handleClose}
            title="Cancel"
          >
            {t("cancel")}
          </button>
          {isOfficeModalOpen && (
            <OfficesModal
              isOfficeModalOpen={isOfficeModalOpen}
              setIsOfficeModalOpen={setIsOfficeModalOpen}
              selectedOfficeIds={selectedOfficeIds}
              setSelectedOfficeIds={setSelectedOfficeIds}
              offices={officeData ? officeData : []}
            />
          )}
          {isRolesModalOpen && (
            <RolesModal
              isRolesModalOpen={isRolesModalOpen}
              setIsRolesModalOpen={setIsRolesModalOpen}
              designationIds={designationIds}
              setDesignationIds={setDesignationIds}
              userRoles={staffRoleData ? staffRoleData : []}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default withTranslation()(UploadModal);
